import { useState } from "react";
import { Link, useParams, useLocation } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Package,
  Pencil,
  Archive,
  Trash2,
  Trophy,
  XOctagon,
  Target,
  Sparkles,
  ChevronRight,
  Banknote,
  Briefcase,
  CheckCircle2,
  XCircle,
  Layers,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import ProductFormSheet from "@/components/Crm/ProductFormSheet";
import ProductOffersEditor from "@/components/Crm/ProductOffersEditor";

function formatBRL(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatCompact(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n) || n === 0) return "R$ 0";
  if (Math.abs(n) >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1).replace(".", ",")}M`;
  if (Math.abs(n) >= 1_000) return `R$ ${(n / 1_000).toFixed(1).replace(".", ",")}k`;
  return `R$ ${n.toFixed(0)}`;
}

// Classes completas (não interpoladas) — o Tailwind faz purge por texto literal.
const DEALS_CARD_SPAN = ["lg:col-span-3", "lg:col-span-2", "lg:col-span-1"];

function CrmProductDetailBody({ id }: { id: number }) {
  const [, navigate] = useLocation();
  const { can } = usePermissions();
  const canManage = can("crm:products_manage");

  const productQuery = trpc.crm.products.get.useQuery({ id });
  const statsQuery = trpc.crm.products.stats.useQuery({ productId: id });
  const recentQuery = trpc.crm.products.recentDeals.useQuery({ productId: id, limit: 10 });
  const offersQuery = trpc.crm.productOffers.listByProduct.useQuery({ productId: id });

  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  const utils = trpc.useUtils();
  const archive = trpc.crm.products.archive.useMutation({
    onSuccess: () => {
      toast.success("Produto arquivado");
      utils.crm.products.get.invalidate({ id });
      utils.crm.products.list.invalidate();
      navigate("/crm/products");
    },
  });
  const del = trpc.crm.products.delete.useMutation({
    onSuccess: () => {
      toast.success("Produto excluído");
      navigate("/crm/products");
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao excluir produto")),
  });

  if (productQuery.isLoading) {
    return (
      <div className="space-y-4 max-w-6xl">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-40" />
        <Skeleton className="h-48" />
      </div>
    );
  }
  if (!productQuery.data) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <p className="font-semibold">Produto não encontrado.</p>
          <Link
            href="/crm/products"
            className="text-sm text-primary hover:underline mt-2 inline-block"
          >
            Voltar ao catálogo
          </Link>
        </CardContent>
      </Card>
    );
  }

  const { product, category } = productQuery.data;
  const attrs = (product.attributes ?? {}) as Record<string, any>;
  const attrEntries = Object.entries(attrs);
  const offers = offersQuery.data ?? [];
  const offerPrices = offers.map((o: any) => Number(o.price ?? 0));
  const offerMin = offerPrices.length > 0 ? Math.min(...offerPrices) : null;
  const offerMax = offerPrices.length > 0 ? Math.max(...offerPrices) : null;
  // Quantos cards laterais ocupam colunas na grade de 3 (ofertas, diferenciais).
  const sideCards = (canManage || offers.length > 0 ? 1 : 0) + (attrEntries.length > 0 ? 1 : 0);
  const stats = statsQuery.data;
  const totalDeals = stats?.totalDeals ?? 0;
  const winRate =
    totalDeals > 0 && (stats?.wonDeals ?? 0) + (stats?.lostDeals ?? 0) > 0
      ? ((stats?.wonDeals ?? 0) /
          ((stats?.wonDeals ?? 0) + (stats?.lostDeals ?? 0))) *
        100
      : null;

  return (
    <div className="space-y-5 max-w-6xl">
      {/* ── Breadcrumb ─────────────────────────── */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground overflow-x-auto -mx-1 px-1 whitespace-nowrap">
        <Link
          href="/crm/products"
          className="hover:text-foreground flex items-center gap-1 shrink-0"
        >
          <ArrowLeft className="h-3 w-3" />
          Catálogo
        </Link>
        <ChevronRight className="h-3 w-3 shrink-0" />
        {/* Voltar uma etapa tem que voltar FILTRADO — o catálogo lê o
            ?categoryId= da URL. Guardar em category?.id (e não só no nome):
            vem de leftJoin, então é null quando não há categoria. */}
        {category?.id ? (
          <Link
            href={`/crm/products?categoryId=${category.id}`}
            className="hover:text-foreground flex items-center gap-1 shrink-0"
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: category.color ?? "#64748b" }}
            />
            {category.name}
          </Link>
        ) : (
          <Link
            href="/crm/products?categoryId=none"
            className="hover:text-foreground shrink-0"
          >
            Sem categoria
          </Link>
        )}
        <ChevronRight className="h-3 w-3 shrink-0" />
        <span className="truncate max-w-[200px]">{product.name}</span>
      </div>

      {/* ── Hero ───────────────────────────────── */}
      <Card>
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col md:flex-row gap-5">
            {/* Imagem */}
            <div className="shrink-0 w-full md:w-48">
              <div className="aspect-[4/3] md:aspect-square w-full rounded-lg border bg-muted overflow-hidden">
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                    <Package className="h-12 w-12 opacity-30" />
                  </div>
                )}
              </div>
            </div>

            {/* Texto */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {category?.name && (
                      <Badge
                        style={{
                          backgroundColor: category.color ?? "#64748b",
                          color: "white",
                        }}
                      >
                        {category.name}
                      </Badge>
                    )}
                    {product.sku && (
                      <Badge variant="outline" className="font-mono text-[10px]">
                        {product.sku}
                      </Badge>
                    )}
                    {product.archived && (
                      <Badge variant="secondary">
                        <Archive className="h-3 w-3 mr-1" />
                        Arquivado
                      </Badge>
                    )}
                  </div>
                  <h1 className="text-xl sm:text-2xl font-bold leading-tight break-words">
                    {product.name}
                  </h1>
                  {product.description && (
                    <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap break-words">
                      {product.description}
                    </p>
                  )}
                </div>
                {canManage && (
                  <div className="flex gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditOpen(true)}
                    >
                      <Pencil className="h-3.5 w-3.5 sm:mr-1.5" />
                      <span className="hidden sm:inline">Editar</span>
                    </Button>
                    {!product.archived && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirmArchive(true)}
                      >
                        <Archive className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => setConfirmDelete(true)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>

              <Separator className="my-3" />

              {/* Sem ofertas, o bloco é o de sempre (preço sugerido). Com
                  ofertas, o preço do produto deixa de ser a resposta certa —
                  passa a ser "a partir de", com a faixa embaixo. */}
              <div className="flex items-end gap-6">
                <div>
                  <div className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground">
                    {offers.length > 0 ? "A partir de" : "Preço sugerido"}
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold tabular-nums flex items-center gap-2">
                    <Banknote className="h-5 w-5 text-muted-foreground" />
                    {formatBRL(offers.length > 0 ? offerMin : product.price)}
                    {offers.length > 0 && (
                      <Badge variant="secondary" className="ml-1">
                        {offers.length} {offers.length === 1 ? "oferta" : "ofertas"}
                      </Badge>
                    )}
                  </div>
                  {offers.length > 0 && offerMin !== offerMax && (
                    <div className="text-sm text-muted-foreground tabular-nums mt-0.5">
                      {formatBRL(offerMin)} – {formatBRL(offerMax)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Stats ─────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Em oportunidades"
          value={String(totalDeals)}
          icon={Briefcase}
          tint="primary"
        />
        <StatCard
          label="Fechados"
          value={String(stats?.wonDeals ?? 0)}
          icon={Trophy}
          tint="emerald"
        />
        <StatCard
          label="Perdidos"
          value={String(stats?.lostDeals ?? 0)}
          icon={XOctagon}
          tint="red"
        />
        <StatCard
          label="Receita ganha"
          value={formatCompact(stats?.wonRevenue ?? 0)}
          sub={winRate !== null ? `${winRate.toFixed(0)}% win rate` : undefined}
          icon={Target}
        />
      </div>

      {/* ── Ofertas + Diferenciais + Últimos deals ──────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* O card de ofertas aparece sempre para quem pode gerenciar (é onde se
            cria a primeira); para os demais, só quando há o que mostrar. */}
        {(canManage || offers.length > 0) && (
          <Card className="lg:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Layers className="h-4 w-4" />
                Ofertas
                {offers.length > 0 && <Badge variant="secondary">{offers.length}</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ProductOffersEditor productId={id} canManage={canManage} />
            </CardContent>
          </Card>
        )}

        {attrEntries.length > 0 && (
          <Card className="lg:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                Diferenciais
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {attrEntries.map(([k, v]) => (
                <div
                  key={k}
                  className="flex items-center justify-between gap-2 border rounded-md px-3 py-2 bg-muted/30"
                >
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">
                    {k}
                  </span>
                  <span className="text-sm font-medium text-right truncate max-w-[60%]">
                    {String(v)}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* O span das oportunidades é o que sobra: cada card lateral presente
            (ofertas, diferenciais) consome uma coluna das três.
            Mapa estático porque o Tailwind não gera classe interpolada. */}
        <Card className={DEALS_CARD_SPAN[sideCards]}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Oportunidades recentes
              {(recentQuery.data?.length ?? 0) > 0 && (
                <Badge variant="secondary">{recentQuery.data!.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {recentQuery.isLoading ? (
              <>
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </>
            ) : (recentQuery.data ?? []).length === 0 ? (
              <div className="text-center py-6">
                <Briefcase className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-40" />
                <p className="text-sm text-muted-foreground">
                  Este produto ainda não foi vinculado a nenhuma oportunidade.
                </p>
              </div>
            ) : (
              (recentQuery.data ?? []).map((row: any) => {
                const d = row.deal;
                const link = row.link;
                const lineTotal =
                  Number(link.quantity ?? 1) *
                  Number(link.unitPrice ?? 0) *
                  ((100 - Number(link.discountPct ?? 0)) / 100);
                const isWon = d.status === "won";
                const isLost = d.status === "lost";
                return (
                  <Link
                    key={d.id}
                    href={`/crm/deals/${d.id}`}
                    className={`block rounded-lg border p-3 hover:bg-muted/40 transition relative overflow-hidden ${
                      isWon
                        ? "border-emerald-200 dark:border-emerald-900"
                        : isLost
                        ? "border-red-200 dark:border-red-900 opacity-70"
                        : ""
                    }`}
                  >
                    {isWon && (
                      <div className="absolute inset-y-0 left-0 w-1 bg-emerald-500" />
                    )}
                    {isLost && (
                      <div className="absolute inset-y-0 left-0 w-1 bg-red-500" />
                    )}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate">{d.title}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          {isWon && (
                            <Badge className="bg-emerald-600 h-5 text-[10px]">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Fechada
                            </Badge>
                          )}
                          {isLost && (
                            <Badge className="bg-red-600 h-5 text-[10px]">
                              <XCircle className="h-3 w-3 mr-1" />
                              Perdida
                            </Badge>
                          )}
                          {!isWon && !isLost && (
                            <Badge variant="outline" className="h-5 text-[10px]">
                              Em aberto
                            </Badge>
                          )}
                          <span>
                            {link.quantity}× {formatBRL(link.unitPrice)}
                          </span>
                          {link.discountPct > 0 && (
                            <span className="text-red-600">
                              -{link.discountPct}%
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-base font-bold tabular-nums">
                          {formatBRL(lineTotal)}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Dialogs ──────────────────────────── */}
      <ProductFormSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        productId={id}
      />

      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Arquivar produto?</AlertDialogTitle>
            <AlertDialogDescription>
              O produto deixará de aparecer para novas oportunidades. As vinculações existentes
              continuam ativas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => archive.mutate({ id })}>
              Arquivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir produto?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é permanente. Se o produto estiver vinculado a oportunidades,
              você precisará arquivá-lo em vez disso.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => del.mutate({ id })}
              className="bg-destructive hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  icon: Icon,
  tint = "default",
}: {
  label: string;
  value: string;
  sub?: string;
  icon: any;
  tint?: "default" | "primary" | "emerald" | "red";
}) {
  const tintMap = {
    default: "bg-muted text-muted-foreground",
    primary: "bg-primary/10 text-primary",
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    red: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
  };
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {label}
            </div>
            <div className="mt-1 text-2xl font-bold tabular-nums truncate">{value}</div>
            {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
          </div>
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${tintMap[tint]}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function CrmProductDetail() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  return (
    <DashboardLayout>
      <PermissionGuard permission="crm:products_view">
        {Number.isFinite(id) ? (
          <CrmProductDetailBody id={id} />
        ) : (
          <div className="py-12 text-center">ID inválido.</div>
        )}
      </PermissionGuard>
    </DashboardLayout>
  );
}
